// ============================================================================
// COMPLIANCE SERVICE
// ============================================================================
// API service for compliance module
// Backend: AssetManager.Api/Controllers/ComplianceController.cs
// ============================================================================

import { authFetch } from '@/lib/auth-fetch';
import type {
  ComplianceCheck,
  CreateComplianceCheck,
  UpdateComplianceCheck,
  AssetCompliance,
  CompleteCompliance,
  BulkAssignCompliance,
  BulkCompleteCompliance,
  ComplianceDashboard,
  ComplianceHistory,
  ComplianceDefault,
  CreateComplianceDefault,
  UpdateComplianceDefault,
  ComplianceStatistics,
  ComplianceFilters,
} from '@/types/compliance';

const API_BASE = '/api/compliance';

class ComplianceService {
  // ============================================================================
  // DASHBOARD
  // ============================================================================

  async getDashboard(): Promise<ComplianceDashboard> {
    const response = await authFetch(`${API_BASE}/dashboard`);
    if (!response.ok) throw new Error('Failed to fetch compliance dashboard');
    return response.json();
  }

  async getUpcomingDeadlines(): Promise<any[]> {
    const response = await authFetch(`${API_BASE}/upcoming-deadlines`);
    if (!response.ok) throw new Error('Failed to fetch upcoming deadlines');
    return response.json();
  }

  async getStatistics(): Promise<ComplianceStatistics> {
    const response = await authFetch(`${API_BASE}/statistics`);
    if (!response.ok) throw new Error('Failed to fetch compliance statistics');
    return response.json();
  }

  // ============================================================================
  // COMPLIANCE CHECKS (Templates)
  // ============================================================================

  async getComplianceChecks(): Promise<ComplianceCheck[]> {
    const response = await authFetch(`${API_BASE}/checks`);
    if (!response.ok) throw new Error('Failed to fetch compliance checks');
    return response.json();
  }

  async getComplianceCheck(id: number): Promise<ComplianceCheck> {
    const response = await authFetch(`${API_BASE}/checks/${id}`);
    if (!response.ok) throw new Error('Failed to fetch compliance check');
    return response.json();
  }

  async createComplianceCheck(data: CreateComplianceCheck): Promise<ComplianceCheck> {
    const response = await authFetch(`${API_BASE}/checks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to create compliance check: ${error}`);
    }
    return response.json();
  }

  async updateComplianceCheck(id: number, data: UpdateComplianceCheck): Promise<void> {
    const response = await authFetch(`${API_BASE}/checks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to update compliance check: ${error}`);
    }
  }

  async deleteComplianceCheck(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE}/checks/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete compliance check: ${error}`);
    }
  }

  async duplicateComplianceCheck(id: number): Promise<ComplianceCheck> {
    const response = await authFetch(`${API_BASE}/checks/${id}/duplicate`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to duplicate compliance check: ${error}`);
    }
    return response.json();
  }

  // ============================================================================
  // ASSET COMPLIANCES
  // ============================================================================

  async getAssetCompliances(filters?: ComplianceFilters): Promise<AssetCompliance[]> {
    const params = new URLSearchParams();
    
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.priority && filters.priority.length > 0) {
      filters.priority.forEach(p => params.append('priority', p));
    }
    if (filters?.type && filters.type.length > 0) {
      filters.type.forEach(t => params.append('type', t));
    }
    if (filters?.location && filters.location.length > 0) {
      filters.location.forEach(l => params.append('location', l));
    }
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.searchTerm) params.append('searchTerm', filters.searchTerm);

    const response = await authFetch(`${API_BASE}/asset-compliances?${params}`);
    if (!response.ok) throw new Error('Failed to fetch asset compliances');
    return response.json();
  }

  async getAssetCompliance(id: number): Promise<AssetCompliance> {
    const response = await authFetch(`${API_BASE}/asset-compliances/${id}`);
    if (!response.ok) throw new Error('Failed to fetch asset compliance');
    return response.json();
  }

  async getAssetCompliancesByAsset(assetId: string): Promise<AssetCompliance[]> {
    const response = await authFetch(`${API_BASE}/asset-compliances/asset/${assetId}`);
    if (!response.ok) throw new Error('Failed to fetch asset compliances');
    return response.json();
  }

  async completeCompliance(id: number, data: CompleteCompliance): Promise<void> {
    const response = await authFetch(`${API_BASE}/asset-compliances/${id}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to complete compliance check: ${error}`);
    }
  }

  async deleteAssetCompliance(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE}/asset-compliances/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete asset compliance: ${error}`);
    }
  }

  // ============================================================================
  // BULK OPERATIONS
  // ============================================================================

  async bulkAssignCompliance(data: BulkAssignCompliance): Promise<{ assignedCount: number }> {
    const response = await authFetch(`${API_BASE}/bulk-assign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to bulk assign compliance: ${error}`);
    }
    return response.json();
  }

  async bulkCompleteCompliance(data: BulkCompleteCompliance): Promise<{ completedCount: number }> {
    const response = await authFetch(`${API_BASE}/bulk-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to bulk complete compliance: ${error}`);
    }
    return response.json();
  }

  async bulkDeleteCompliance(ids: number[]): Promise<{ deletedCount: number }> {
    const response = await authFetch(`${API_BASE}/bulk-delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to bulk delete compliance: ${error}`);
    }
    return response.json();
  }

  // ============================================================================
  // HISTORY
  // ============================================================================

  async getAssetComplianceHistory(assetId: string): Promise<ComplianceHistory[]> {
    const response = await authFetch(`${API_BASE}/history/asset/${assetId}`);
    if (!response.ok) throw new Error('Failed to fetch compliance history');
    return response.json();
  }

  async getComplianceCheckHistory(checkId: number): Promise<ComplianceHistory[]> {
    const response = await authFetch(`${API_BASE}/history/check/${checkId}`);
    if (!response.ok) throw new Error('Failed to fetch compliance check history');
    return response.json();
  }

  // ============================================================================
  // COMPLIANCE DEFAULTS
  // ============================================================================

  async getComplianceDefaults(): Promise<ComplianceDefault[]> {
    const response = await authFetch(`${API_BASE}/defaults`);
    if (!response.ok) throw new Error('Failed to fetch compliance defaults');
    return response.json();
  }

  async getComplianceDefaultsByCategory(category: string): Promise<ComplianceDefault[]> {
    const response = await authFetch(`${API_BASE}/defaults/category/${encodeURIComponent(category)}`);
    if (!response.ok) throw new Error('Failed to fetch compliance defaults for category');
    return response.json();
  }

  async createComplianceDefault(data: CreateComplianceDefault): Promise<ComplianceDefault> {
    const response = await authFetch(`${API_BASE}/defaults`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to create compliance default: ${error}`);
    }
    return response.json();
  }

  async updateComplianceDefault(id: number, data: UpdateComplianceDefault): Promise<void> {
    const response = await authFetch(`${API_BASE}/defaults/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to update compliance default: ${error}`);
    }
  }

  async deleteComplianceDefault(id: number): Promise<void> {
    const response = await authFetch(`${API_BASE}/defaults/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete compliance default: ${error}`);
    }
  }

  async applyDefaultsToAsset(assetId: string): Promise<{ assignedCount: number }> {
    const response = await authFetch(`${API_BASE}/defaults/apply/${assetId}`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to apply defaults to asset: ${error}`);
    }
    return response.json();
  }

  // ============================================================================
  // EVIDENCE MANAGEMENT
  // ============================================================================

  async getEvidence(assetComplianceId: number): Promise<any[]> {
    const response = await authFetch(`${API_BASE}/asset-compliances/${assetComplianceId}/evidence`);
    if (!response.ok) throw new Error('Failed to fetch evidence');
    return response.json();
  }

  async uploadEvidence(assetComplianceId: number, file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await authFetch(`${API_BASE}/asset-compliances/${assetComplianceId}/evidence`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to upload evidence: ${error}`);
    }
  }

  async deleteEvidence(evidenceId: number): Promise<void> {
    const response = await authFetch(`${API_BASE}/evidence/${evidenceId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to delete evidence: ${error}`);
    }
  }

  async downloadEvidence(evidenceId: number): Promise<Blob> {
    const response = await authFetch(`${API_BASE}/evidence/${evidenceId}/download`);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to download evidence: ${error}`);
    }
    return response.blob();
  }

  // ============================================================================
  // EXPORT
  // ============================================================================

  async exportCompliance(filters?: ComplianceFilters, format: 'csv' | 'excel' | 'pdf' = 'excel'): Promise<Blob> {
    const params = new URLSearchParams();
    params.append('format', format);
    
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);

    const response = await authFetch(`${API_BASE}/export?${params}`);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to export compliance data: ${error}`);
    }
    return response.blob();
  }

  // ============================================================================
  // COMPLIANCE ACTIONS (NEW)
  // ============================================================================

  async recordInspection(data: {
    assetComplianceId: number;
    inspectionDate: string;
    inspectorName: string;
    outcome: string;
    notes?: string;
    cost?: number;
    serviceProvider?: string;
    nextInspectionDate?: string;
  }): Promise<any> {
    const response = await authFetch(`${API_BASE}/inspections/record`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to record inspection: ${error}`);
    }
    return response.json();
  }

  async recordInspectionBulk(items: Array<{
    assetComplianceId: number;
    inspectionDate: string;
    inspectorName: string;
    outcome: string;
    notes?: string;
    cost?: number;
    serviceProvider?: string;
  }>): Promise<{ recordedCount: number }> {
    const response = await authFetch(`${API_BASE}/inspections/record/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to record inspections: ${error}`);
    }
    return response.json();
  }

  async scheduleInspection(data: {
    assetComplianceId: number;
    scheduledDate: string;
    assignedInspector?: string;
    schedulingNotes?: string;
    sendNotification: boolean;
  }): Promise<any> {
    const response = await authFetch(`${API_BASE}/inspections/schedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to schedule inspection: ${error}`);
    }
    return response.json();
  }

  async scheduleInspectionBulk(items: Array<{
    assetComplianceId: number;
    scheduledDate: string;
    assignedInspector?: string;
    schedulingNotes?: string;
  }>): Promise<{ scheduledCount: number }> {
    const response = await authFetch(`${API_BASE}/inspections/schedule/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to schedule inspections: ${error}`);
    }
    return response.json();
  }

  async getComplianceHistory(assetComplianceId: number): Promise<any[]> {
    const response = await authFetch(`${API_BASE}/${assetComplianceId}/history`);
    if (!response.ok) throw new Error('Failed to fetch compliance history');
    return response.json();
  }

  async exportHistory(assetComplianceId: number, format: 'pdf' | 'excel'): Promise<Blob> {
    const response = await authFetch(`${API_BASE}/${assetComplianceId}/history/export?format=${format}`);
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to export history: ${error}`);
    }
    return response.blob();
  }

  async markNonApplicable(data: {
    assetComplianceId: number;
    reason: string;
    detailedExplanation: string;
    effectiveDate: string;
    expiryDate?: string;
    reviewedBy?: string;
  }): Promise<any> {
    const response = await authFetch(`${API_BASE}/exemptions/mark`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to mark as non-applicable: ${error}`);
    }
    return response.json();
  }

  async markNonApplicableBulk(items: Array<{
    assetComplianceId: number;
    reason: string;
    detailedExplanation: string;
    effectiveDate: string;
  }>): Promise<{ markedCount: number }> {
    const response = await authFetch(`${API_BASE}/exemptions/mark/bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to mark items as non-applicable: ${error}`);
    }
    return response.json();
  }

  async reverseExemption(exemptionId: number): Promise<void> {
    const response = await authFetch(`${API_BASE}/exemptions/${exemptionId}/reverse`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to reverse exemption: ${error}`);
    }
  }

  async getExemptions(assetComplianceId: number): Promise<any[]> {
    const response = await authFetch(`${API_BASE}/${assetComplianceId}/exemptions`);
    if (!response.ok) throw new Error('Failed to fetch exemptions');
    return response.json();
  }

  async deferDueDate(data: {
    assetComplianceId: number;
    currentDueDate: string;
    newDueDate: string;
    reason: string;
    approvalRequired: boolean;
    approver?: string;
  }): Promise<any> {
    const response = await authFetch(`${API_BASE}/defer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to defer due date: ${error}`);
    }
    return response.json();
  }

  async uploadInspectionDocuments(inspectionId: number, files: File[]): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await authFetch(`${API_BASE}/inspections/${inspectionId}/documents`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to upload documents: ${error}`);
    }
  }

  async uploadExemptionDocuments(exemptionId: number, files: File[]): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const response = await authFetch(`${API_BASE}/exemptions/${exemptionId}/documents`, {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Failed to upload documents: ${error}`);
    }
  }
}

export const complianceService = new ComplianceService();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get compliance items for a specific asset
 * Maps AssetCompliance to ComplianceItem format for UI components
 */
export async function getComplianceItemsForAsset(assetId: string): Promise<any[]> {
  const assetCompliances = await complianceService.getAssetCompliancesByAsset(assetId);
  
  // Map to ComplianceItem format
  return assetCompliances.map((ac) => ({
    id: ac.id.toString(),
    assetId: ac.assetId,
    assetName: ac.assetName,
    regulation: ac.complianceCheckName,
    description: ac.complianceType,
    frequency: 'Annually', // Default value, would need to be fetched from ComplianceCheck if needed
    status: ac.status,
    nextInspectionDate: ac.nextDueDate || ac.dueDate,
    lastInspectionDate: ac.completedDate,
    lastInspectionResult: ac.result,
    assignedInspector: ac.assignedByName,
    certificationNumber: undefined, // Not available in AssetCompliance
    priority: ac.priority,
  }));
}
